import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  ChevronRight, TrendingUp, CheckCircle, Shield, 
  Users, Gavel, FileText, Send
} from 'lucide-react';

export default function DevenirVendeur() {
  const { API } = useContext(AppContext);
  const [formData, setFormData] = useState({
    entreprise: '',
    siret: '',
    adresse: '',
    contact_nom: '',
    contact_fonction: '',
    contact_email: '',
    contact_tel: '',
    type_materiel: '',
    nb_lots_envisages: '',
    delai_souhaite: '',
    message: '',
    acceptation_conditions: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.acceptation_conditions) {
      toast.error('Vous devez accepter les conditions');
      return;
    }

    if (!formData.entreprise || !formData.siret || !formData.contact_email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/encheres/devenir-vendeur`, formData);
      setSubmitted(true);
      toast.success('Votre demande a été envoyée');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de l\'envoi';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Schema.org for the service page
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Vente de matériel géotechnique par courtage aux enchères",
    "provider": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "url": "https://geots.consulting"
    },
    "description": "Service de vente par courtage aux enchères pour le matériel géotechnique et de fondations spéciales. Dépôt gratuit, commission uniquement au succès.",
    "areaServed": {
      "@type": "Country",
      "name": "France"
    },
    "serviceType": "Courtage aux enchères",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR",
      "description": "Dépôt de lots gratuit, commission uniquement en cas de vente"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://geots.consulting/" },
      { "@type": "ListItem", "position": 2, "name": "Enchères", "item": "https://geots.consulting/encheres" },
      { "@type": "ListItem", "position": 3, "name": "Devenir vendeur", "item": "https://geots.consulting/encheres/devenir-vendeur" }
    ]
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Helmet>
          <title>Demande envoyée | Devenir vendeur | GEOTS Enchères</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <div className="container mx-auto px-4 py-16">
          <div className="max-w-lg mx-auto text-center">
            <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#0A2540] mb-4">
              Demande envoyée !
            </h1>
            <p className="text-gray-600 mb-8">
              Votre demande a bien été reçue. Nous revenons vers vous pour qualifier 
              votre besoin, puis nous vous transmettrons vos accès.
            </p>
            <Link to="/encheres">
              <Button>Retour aux enchères</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Devenir vendeur | Vendre du matériel aux enchères | GEOTS</title>
        <meta 
          name="description" 
          content="Vendez votre matériel géotechnique aux enchères. Dépôt gratuit, aucun droit d'entrée, commission uniquement en cas de vente." 
        />
        <link rel="canonical" href="https://geots.consulting/encheres/devenir-vendeur" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Devenir vendeur - Vendre du matériel aux enchères" />
        <meta property="og:description" content="Dépôt gratuit, aucun droit d'entrée. Vendez votre matériel géotechnique aux enchères avec GEOTS." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://geots.consulting/encheres/devenir-vendeur" />
        <meta property="og:site_name" content="GEOTS Consulting" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Devenir vendeur | GEOTS Enchères" />
        <meta name="twitter:description" content="Vendez votre matériel géotechnique. Dépôt gratuit, commission au succès." />
        
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Header */}
      <div className="bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#1a3a5c] text-white">
        <div className="container mx-auto px-4 py-12">
          <nav className="flex items-center gap-2 text-sm text-gray-300 mb-6">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/encheres" className="hover:text-white">Enchères</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium">Devenir vendeur</span>
          </nav>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#F0A22E]/20 px-4 py-2 rounded-full mb-4">
              <TrendingUp className="w-5 h-5 text-[#F0A22E]" />
              <span className="text-sm font-medium text-[#F0A22E]">Vente par courtage aux enchères</span>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Vendez votre matériel aux enchères
            </h1>
            <p className="text-lg text-gray-300 mb-8">
              Foreuses, sondeuses, enregistreurs de forage, pompes d&apos;injection et outillage géotechnique. 
              Touchez des acheteurs qualifiés dans toute la France.
            </p>

            {/* Benefits */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-white/10 rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-green-400 mb-2" />
                <p className="font-medium">Dépôt gratuit</p>
                <p className="text-sm text-gray-300">Aucun droit d&apos;entrée</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <Shield className="w-6 h-6 text-blue-400 mb-2" />
                <p className="font-medium">Commission au succès</p>
                <p className="text-sm text-gray-300">Payez uniquement si vente</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <Users className="w-6 h-6 text-[#F0A22E] mb-2" />
                <p className="font-medium">Acheteurs qualifiés</p>
                <p className="text-sm text-gray-300">Dossiers vérifiés</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Demander un accès vendeur</CardTitle>
                <p className="text-sm text-gray-500">
                  L&apos;accès vendeur est délivré par GEOTS après étude de votre demande.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Company Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#0A2540] border-b pb-2">Votre entreprise</h3>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="entreprise">Raison sociale *</Label>
                        <Input
                          id="entreprise"
                          value={formData.entreprise}
                          onChange={(e) => handleChange('entreprise', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="siret">SIRET *</Label>
                        <Input
                          id="siret"
                          value={formData.siret}
                          onChange={(e) => handleChange('siret', e.target.value)}
                          placeholder="123 456 789 00012"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="adresse">Adresse</Label>
                      <Input
                        id="adresse"
                        value={formData.adresse}
                        onChange={(e) => handleChange('adresse', e.target.value)}
                        placeholder="Adresse complète"
                      />
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#0A2540] border-b pb-2">Contact</h3>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contact_nom">Nom *</Label>
                        <Input
                          id="contact_nom"
                          value={formData.contact_nom}
                          onChange={(e) => handleChange('contact_nom', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_fonction">Fonction</Label>
                        <Input
                          id="contact_fonction"
                          value={formData.contact_fonction}
                          onChange={(e) => handleChange('contact_fonction', e.target.value)}
                          placeholder="Ex: Directeur technique"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contact_email">Email *</Label>
                        <Input
                          id="contact_email"
                          type="email"
                          value={formData.contact_email}
                          onChange={(e) => handleChange('contact_email', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_tel">Téléphone *</Label>
                        <Input
                          id="contact_tel"
                          type="tel"
                          value={formData.contact_tel}
                          onChange={(e) => handleChange('contact_tel', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Material */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#0A2540] border-b pb-2">Votre projet de vente</h3>
                    
                    <div>
                      <Label htmlFor="type_materiel">Type de matériel à vendre *</Label>
                      <Textarea
                        id="type_materiel"
                        value={formData.type_materiel}
                        onChange={(e) => handleChange('type_materiel', e.target.value)}
                        placeholder="Ex: Foreuse Comacchio MC 900 année 2018, enregistreur Jean Lutz..."
                        rows={3}
                        required
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="nb_lots">Nombre de lots envisagés</Label>
                        <Select 
                          value={formData.nb_lots_envisages} 
                          onValueChange={(v) => handleChange('nb_lots_envisages', v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 lot</SelectItem>
                            <SelectItem value="2-5">2 à 5 lots</SelectItem>
                            <SelectItem value="6-20">6 à 20 lots</SelectItem>
                            <SelectItem value="plus de 20">Plus de 20 lots</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="delai">Délai souhaité</Label>
                        <Input
                          id="delai"
                          value={formData.delai_souhaite}
                          onChange={(e) => handleChange('delai_souhaite', e.target.value)}
                          placeholder="Ex: Sous 2 mois"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message">Message (optionnel)</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        placeholder="Précisions sur votre projet, questions..."
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* Conditions */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Checkbox 
                        id="conditions" 
                        checked={formData.acceptation_conditions}
                        onCheckedChange={(checked) => handleChange('acceptation_conditions', checked)}
                      />
                      <Label htmlFor="conditions" className="text-sm leading-relaxed">
                        J&apos;accepte les{' '}
                        <Link to="/encheres/conditions" className="text-[#F0A22E] hover:underline" target="_blank">
                          conditions générales de vente par courtage aux enchères
                        </Link>{' '}
                        et je comprends que l&apos;accès vendeur est soumis à validation par GEOTS.
                      </Label>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#F0A22E] hover:bg-[#d89426] text-lg py-6"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <span className="animate-pulse">Envoi en cours...</span>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Envoyer ma demande
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Comment ça marche ?</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-4">
                  <li className="flex gap-3">
                    <span className="bg-[#F0A22E] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                    <div>
                      <p className="font-medium">Vous déposez votre demande</p>
                      <p className="text-sm text-gray-500">Gratuit et sans engagement</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-[#F0A22E] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                    <div>
                      <p className="font-medium">Nous qualifions votre besoin</p>
                      <p className="text-sm text-gray-500">Échange téléphonique sous 48h</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-[#F0A22E] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                    <div>
                      <p className="font-medium">Vous recevez vos accès</p>
                      <p className="text-sm text-gray-500">Espace vendeur sécurisé</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-[#F0A22E] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                    <div>
                      <p className="font-medium">Vous créez vos lots</p>
                      <p className="text-sm text-gray-500">Photos, description, mise à prix</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-[#F0A22E] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">5</span>
                    <div>
                      <p className="font-medium">Vous choisissez l&apos;acquéreur</p>
                      <p className="text-sm text-gray-500">Après la clôture des enchères</p>
                    </div>
                  </li>
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-[#0A2540] text-white">
              <CardContent className="p-6">
                <Gavel className="w-8 h-8 text-[#F0A22E] mb-4" />
                <h3 className="font-bold text-lg mb-2">Pourquoi vendre aux enchères ?</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Visibilité maximale auprès des professionnels
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Prix de marché obtenu par la compétition
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Délai de vente maîtrisé
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Acheteurs pré-qualifiés
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Vous restez maître de la décision
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-3">
                  Des questions sur le fonctionnement ?
                </p>
                <Link to="/encheres/conditions">
                  <Button variant="outline" size="sm">
                    Lire les conditions
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
